import type { BuilderSettings, ExerciseKind } from '@features/Session';
import { takeUpTo, type CardWeight } from './cardWeight';

export const EXERCISE_ORDER = ['input', 'reverse', 'mcq', 'cloze'] as const;

export const TRAINING_PRESETS = [
  { id: 'warmup', exercises: ['input', 'mcq'] },
  { id: 'balanced', exercises: EXERCISE_ORDER },
  { id: 'challenge', exercises: ['reverse', 'cloze'] },
] as const;

export const MAX_SESSION_CARDS = 100;

type TrainingPresetId = (typeof TRAINING_PRESETS)[number]['id'];

/** Состав из адреса (`?preset=warmup&n=10&boss=0` — ссылка из письма через /repeat).
 *  Упражнения действуют, только если у модуля нет сохранённого состава; число карточек и `boss` —
 *  выбор входа и действуют всегда (см. resolveBuilderStart). */
export interface BuilderDefaults {
  exercises?: readonly ExerciseKind[];
  count?: number;
  withBoss?: boolean;
  /** `?cards=today` — из списка «На чём потренируемся?»: отмечен фильтр «На сегодня» (2026-10-21). */
  cards?: 'today';
}

export function readBuilderDefaults(params: URLSearchParams): BuilderDefaults | undefined {
  const preset = TRAINING_PRESETS.find((item) => item.id === params.get('preset'));
  const n = Number(params.get('n'));
  const count = Number.isInteger(n) && n >= 1 && n <= MAX_SESSION_CARDS ? n : undefined;
  const boss = params.get('boss');
  const withBoss = boss === '0' ? false : boss === '1' ? true : undefined;
  const cards = params.get('cards') === 'today' ? ('today' as const) : undefined;
  if (!preset && count === undefined && withBoss === undefined && !cards) return undefined;
  return { exercises: preset?.exercises, count, withBoss, ...(cards ? { cards } : {}) };
}

/** Restore legacy random mixes as the full, editable exercise selection. */
export function parseExercises(saved?: string[] | null): {
  selected: Set<ExerciseKind>;
} {
  if (saved?.includes('random')) {
    return { selected: new Set(EXERCISE_ORDER) };
  }

  const selected = EXERCISE_ORDER.filter((kind) => saved?.includes(kind));
  return {
    selected: new Set(selected.length > 0 ? selected : EXERCISE_ORDER),
  };
}

/** С чего открывается конструктор: что берём из адреса, что — из сохранённого состава. */
export interface BuilderStart {
  exercises: Set<ExerciseKind>;
  /** Сохранённый набор, по которому восстанавливаются карточки; null — набор по умолчанию. */
  poolSettings: BuilderSettings | null;
  /** Размер набора по умолчанию: `?n=` из адреса, иначе 20. */
  defaultCount: number;
  requestedCount: number | 'all';
  withBoss: boolean;
  /** С какого фильтра карточек открыться: «На сегодня» — из списка модулей, иначе «Все». */
  cardFilter: 'today' | 'all';
}

/**
 * Упражнения из адреса — только без сохранённого состава: формат выбирал ученик. Число карточек
 * из адреса главнее сохранённого набора: ссылка «Повторить 10 слов» обещает 10, а набор «все»
 * сохраняет и первая тренировка знакомства (B1, 2026-10-20). Финальный матч из адреса — всегда
 * (решение 2026-10-19: короткая разминка, как в G).
 */
export function resolveBuilderStart(
  savedSettings: BuilderSettings | null,
  defaults?: BuilderDefaults,
): BuilderStart {
  const count = defaults?.count;
  const poolSettings = count === undefined ? savedSettings : null;
  return {
    exercises:
      !savedSettings && defaults?.exercises
        ? new Set(defaults.exercises)
        : parseExercises(savedSettings?.exercises).selected,
    poolSettings,
    defaultCount: count ?? 20,
    requestedCount:
      count ??
      (poolSettings?.stateIds === null
        ? 'all'
        : Math.min(poolSettings?.stateIds.length || 20, MAX_SESSION_CARDS)),
    withBoss: defaults?.withBoss ?? savedSettings?.withBoss ?? true,
    cardFilter: defaults?.cards === 'today' ? 'today' : 'all',
  };
}

export function matchingPreset(
  selected: Set<ExerciseKind>,
): TrainingPresetId | 'custom' {
  const preset = TRAINING_PRESETS.find(
    ({ exercises }) =>
      exercises.length === selected.size && exercises.every((kind) => selected.has(kind)),
  );
  return preset?.id ?? 'custom';
}

const unit: CardWeight = () => 1;

/** IDs represent individual user card states, including separate parts of speech.
 *  Sizes count cards: a state brings one card per sense picked for training.
 *  `defaultCount` — size of the default set (20, or `?n=` from the address). */
export function reconcilePool(
  allIds: string[],
  savedSettings: BuilderSettings | null,
  weight: CardWeight = unit,
  defaultCount = 20,
): Set<string> {
  const availableIds = [...new Set(allIds)];
  const defaultIds = takeUpTo(availableIds, defaultCount, weight);
  if (savedSettings === null) return new Set(defaultIds);
  if (savedSettings.stateIds === null) {
    return new Set(takeUpTo(availableIds, MAX_SESSION_CARDS, weight));
  }

  const available = new Set(availableIds);
  const restored = takeUpTo(
    [...new Set(savedSettings.stateIds)].filter((id) => available.has(id)),
    MAX_SESSION_CARDS,
    weight,
  );
  return new Set(restored.length > 0 ? restored : defaultIds);
}

/** Uniform sampling without replacement, up to `count` cards (see takeUpTo). */
export function sampleIds(
  allIds: string[],
  count: number,
  random: () => number = Math.random,
  weight: CardWeight = unit,
): string[] {
  const ids = [...new Set(allIds)];
  const limit = Math.min(MAX_SESSION_CARDS, Number.isNaN(count) ? 0 : Math.max(0, Math.floor(count)));

  for (let index = 0; index < ids.length; index += 1) {
    const picked = index + Math.floor(random() * (ids.length - index));
    [ids[index], ids[picked]] = [ids[picked], ids[index]];
  }
  return takeUpTo(ids, limit, weight);
}
